import {
  GROCERY_CATEGORIES,
  type GroceryCategory,
  type PantryItem,
  type PantryItemWriteInput,
} from '@recipes/contracts';
import { classifyGroceryCategory, normalizeUnit, parseQuantity } from '@recipes/domain';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@recipes/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/features/shared/form-field';
import { groceryCategoryLabel } from '@/features/shared/labels';

export function PantryItemDialog({
  open,
  onOpenChange,
  item,
  onSubmit,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: PantryItem | null;
  onSubmit: (input: Omit<PantryItemWriteInput, 'householdId'>) => void;
  saving?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [alwaysInStock, setAlwaysInStock] = useState(false);
  const [category, setCategory] = useState<GroceryCategory>('other');
  const [categoryTouched, setCategoryTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? '');
    setQuantity(item?.quantity ?? '');
    setUnit(item?.unitCode ?? item?.unitText ?? '');
    setExpiresAt(item?.expiresAt ?? '');
    setNote(item?.note ?? '');
    setAlwaysInStock(item?.alwaysInStock ?? false);
    setCategory(item?.groceryCategory ?? 'other');
    setCategoryTouched(Boolean(item));
  }, [open, item]);

  useEffect(() => {
    if (!categoryTouched && name.trim()) setCategory(classifyGroceryCategory(name.trim()));
  }, [name, categoryTouched]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{item ? t('pantry.edit') : t('pantry.add')}</DialogTitle>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            const u = unit.trim();
            const catalog = u ? normalizeUnit(u) : undefined;
            onSubmit({
              name: name.trim(),
              quantity: quantity.trim() ? parseQuantity(quantity.trim()) : null,
              unitCode: catalog?.code ?? null,
              unitText: catalog ? null : u || null,
              groceryCategory: category,
              expiresAt: expiresAt || null,
              note: note.trim() || null,
              alwaysInStock,
            });
          }}
        >
          <FormField label={t('pantry.fields.name')} required>
            <Input
              value={name}
              maxLength={200}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              data-testid="pantry-name"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('pantry.fields.quantity')} optionalText={t('common.optional')}>
              <Input
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </FormField>
            <FormField label={t('pantry.fields.unit')} optionalText={t('common.optional')}>
              <Input value={unit} maxLength={40} onChange={(e) => setUnit(e.target.value)} />
            </FormField>
          </div>
          <FormField label={t('pantry.fields.expiresAt')} optionalText={t('common.optional')}>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </FormField>
          <div>
            <Label htmlFor="pantry-category">{t('pantry.fields.category')}</Label>
            <Select
              value={category}
              onValueChange={(v) => {
                setCategory(v as GroceryCategory);
                setCategoryTouched(true);
              }}
            >
              <SelectTrigger id="pantry-category" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROCERY_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {groceryCategoryLabel(t, c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <FormField label={t('pantry.fields.note')} optionalText={t('common.optional')}>
            <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </FormField>
          <div className="flex items-start gap-3">
            <Switch
              id="pantry-always-in-stock"
              checked={alwaysInStock}
              onCheckedChange={setAlwaysInStock}
              data-testid="pantry-always-in-stock"
            />
            <div>
              <Label htmlFor="pantry-always-in-stock">{t('pantry.fields.alwaysInStock')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('pantry.fields.alwaysInStockHint')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={saving}
              disabled={!name.trim()}
              data-testid="pantry-save"
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
